import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Typography, Tag, Spin, Input, DatePicker, Select } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';

import certificateStore from '../../store/CertificateStore';
import employeeStore from '../../store/EmployeeStore';
import tagStore from '../../store/TagStore';
import { openBitrixLink } from '../../utils/links';
import TagSelector from '../../components/Tags/TagSelector';

const { Text, Title } = Typography;

// Single status tag for certificate
const getStatusTag = (expired, hasDeadline) => {
    if (!hasDeadline) return <Tag color="blue">Бессрочный</Tag>;

    const now = dayjs();
    const expDate = dayjs(expired);
    const diffDays = expDate.diff(now, 'day');

    if (diffDays < 0) {
        return <Tag color="red">Просрочен ({Math.abs(diffDays)} д.)</Tag>;
    } else if (diffDays === 0) {
        return <Tag color="red">Истекает сегодня</Tag>;
    } else if (diffDays <= 7) {
        return <Tag color="orange">Осталось {diffDays} д.</Tag>;
    } else if (diffDays <= 30) {
        return <Tag color="gold">Осталось {diffDays} д.</Tag>;
    } else {
        return <Tag color="green">{expDate.format('DD.MM.YYYY')}</Tag>;
    }
};

const CertificatePage = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const certificate = certificateStore.certificates.find((c) => c.id === Number(id));
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    tagStore.fetchTags();
    // Load certificates if store is empty
    if (certificateStore.certificates.length === 0) {
      certificateStore.fetchCertificates(true); // silent mode
    }
    // Load employees if store is empty
    if (employeeStore.employees.length === 0) {
      employeeStore.fetchEmployees();
    }
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({
      name: certificate.name,
      employeeId: certificate.employeeId,
      expired: certificate.expired,
      hasDeadline: certificate.hasDeadline,
      bitrixFolder: certificate.bitrixFolder,
      info: certificate.info || '',
      tagIds: certificate.tagIds || [],
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({});
  };

  const handleSave = () => {
    if (!certificate) return;
    certificateStore.updateCertificate(certificate.id, editedData);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!certificate) return;
    if (window.confirm('Вы уверены, что хотите удалить этот сертификат?')) {
      certificateStore.deleteCertificate(certificate.id);
      navigate(-1);
    }
  };

  // Show spinner while loading
  if (certificateStore.loading || employeeStore.loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show not found only after loading is complete
  if (!certificate) {
    return <div>Удостоверение не найдено</div>;
  }

  // Get employee
  const employee = employeeStore.getEmployeeById(
    isEditing ? editedData.employeeId : certificate.employeeId
  );

  const isExpired = certificate.hasDeadline && dayjs(certificate.expired).isBefore(dayjs(), 'day');
  const bitrixDomain = process.env.REACT_APP_BITRIX_DOMAIN || 'https://tmgdin.bitrix24.ru';

  return (
    <>
      {/* Header with back button and action buttons */}
      <Space style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Назад
        </Button>
        
        {isEditing ? (
          <>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
            >
              Сохранить
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancel}
            >
              Отмена
            </Button>
          </>
        ) : (
          <>
            <Button
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              Редактировать
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              Удалить
            </Button>
          </>
        )}
      </Space>

      {/* Content */}
      <div style={{ maxWidth: 800 }}>
        {/* Date and days left */}
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Срок действия:</Text>
          {isEditing ? (
            <Space>
              <DatePicker
                value={editedData.expired ? dayjs(editedData.expired) : null}
                onChange={(date) => setEditedData({ 
                  ...editedData, 
                  expired: date ? date.format('YYYY-MM-DD') : null,
                  hasDeadline: !!date
                })}
                format="DD.MM.YYYY"
                style={{ width: 200 }}
              />
            </Space>
          ) : (
            <Space size={8}>
              {getStatusTag(certificate.expired, certificate.hasDeadline)}
            </Space>
          )}
        </div>

        {/* Certificate name */}
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Название:</Text>
          {isEditing ? (
            <Input.TextArea
              value={editedData.name}
              onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
              rows={3}
              style={{ width: '100%' }}
              maxLength={100}
              showCount
            />
          ) : (
            <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
              {certificate.name || 'Без названия'}
            </Title>
          )}
        </div>

        {/* Tags */}
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Теги:</Text>
          {isEditing ? (
            <TagSelector
              value={editedData.tagIds}
              onChange={(value) => setEditedData({ ...editedData, tagIds: value })}
            />
          ) : (
            certificate.tagIds && certificate.tagIds.length > 0 ? (
              <Space size={[8, 8]} wrap>
                {tagStore.getTagsByIds(certificate.tagIds).map(tag => (
                  <Tag key={tag.id} color={tag.color}>
                    {tag.name}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Не указаны</Text>
            )
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Описание:</Text>
          {isEditing ? (
            <Input.TextArea
              value={editedData.info}
              onChange={(e) => setEditedData({ ...editedData, info: e.target.value })}
              rows={3}
              placeholder="Описание сертификата"
              style={{ width: '100%' }}
            />
          ) : (
            <Text>{certificate.info || 'Не указано'}</Text>
          )}
        </div>

        {/* Employee */}
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Сотрудник:</Text>
          {isEditing ? (
            <Select
              value={editedData.employeeId}
              onChange={(value) => setEditedData({ ...editedData, employeeId: value })}
              style={{ width: '100%', maxWidth: 400 }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={employeeStore.employees.map(emp => ({
                value: emp.id,
                label: emp.name
              }))}
            />
          ) : (
            <Button
              onClick={() => employee && openBitrixLink(`${bitrixDomain}/company/personal/user/${employee.id}/`)}
              disabled={!employee}
            >
              {employee ? employee.name : 'Не указан'}
            </Button>
          )}
        </div>

        {/* Files link */}
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Связанные файлы:</Text>
          {isEditing ? (
            <Input
              value={editedData.bitrixFolder}
              onChange={(e) => setEditedData({ ...editedData, bitrixFolder: e.target.value })}
              placeholder="Ссылка на папку в Bitrix24"
              style={{ width: '100%' }}
            />
          ) : (
            <Button
              type="primary"
              onClick={() => openBitrixLink(certificate.bitrixFolder)}
              disabled={!certificate.bitrixFolder}
            >
              {certificate.bitrixFolder ? 'Открыть файлы' : 'Ссылка не указана'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
});

export default CertificatePage;
